import { DayPicker } from 'react-day-picker';

import {
  bookingsToDayWithHours,
  googleEventsToDayWithHours,
  buildHoursofTheDaysOfTheMonthObject,
} from '@/utils/availability';
import { AgendaConfiguration, DayWithHours } from '@/api/agendaConfiguration';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useEloApi, ApiError } from '@/api/api';
import { useError } from '@/contexts/ErrorContext';
import { useAuth0, User as AuthUser } from '@auth0/auth0-react';
import { Booking } from '@/api/booking';

import { GoogleCalendarEvent, User } from '@/api/users';
import { Button, Center, Loader } from '@mantine/core';

import './myDayPicker.css';

import { DateTime } from 'luxon';
import { cloneDeep } from 'lodash';
import { useTimezone } from '@/contexts/TimezoneContext';

function getMonthBoundaries(currentMonth: Date): { firstDay: Date; lastDay: Date } {
  const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);

  const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
  lastDay.setHours(23, 59, 59, 999);

  return { firstDay, lastDay };
}

interface MyDayPickerProps {
  selectedDate: Date | undefined;
  setSelectedDate: (selectedDate: Date | undefined) => void;
  agendaConfiguration: AgendaConfiguration;
  bookings: Booking[];
  setBookings: (bookings: Booking[]) => void;
  googleCalendarEvents: GoogleCalendarEvent[];
  setGoogleCalendarEvents: (googleCalendarEvents: GoogleCalendarEvent[]) => void;
  localUser: User;
  extension?: string;
  dayPickerHeight?: number;
  dayPickerWidth?: number;
  selectedHour?: string;
  setSelectedHour?: (hour: string) => void;
  forceAvailable?: { [day: number]: string[] };
  startMonth?: Date;
}

export function MyDayPicker({
  selectedDate,
  setSelectedDate,
  bookings,
  setBookings,
  agendaConfiguration,
  googleCalendarEvents,
  setGoogleCalendarEvents,
  localUser,
  extension = '',
  dayPickerHeight = 0,
  dayPickerWidth = 0,
  selectedHour = '',
  setSelectedHour = (hour) => {},
  forceAvailable = {},
  startMonth = new Date(),
}: MyDayPickerProps) {
  let user: AuthUser | undefined;
  let api;
  if (!extension) {
    const { getAccessTokenSilently, user: authUser } = useAuth0();
    user = authUser;
    api = useEloApi({ getAccessTokenSilently });
  } else {
    api = useEloApi({});
  }
  const localTimeZone = DateTime.local().zoneName;
  // const localTimeZone = 'America/Mexico_City';

  const errorContext = useError();

  const [loading, setLoading] = useState(false);

  const getGoogleCalendarEvents = async () => {
    try {
      const res = await api.getGoogleCalendarEvents(
        getStartOfMonth(currentMonth),
        getEndOfMonth(currentMonth),
        user?.sub ? user?.sub : null,
        extension
      );
      setGoogleCalendarEvents(res);
    } catch (error) {
      console.error(error);
      if (error instanceof ApiError) {
        errorContext.setError(error.message);
      } else {
        throw error;
      }
    }
  };

  const [currentMonth, setCurrentMonth] = useState(startMonth ? startMonth : new Date());
  const prevMonth = useRef(currentMonth);
  const getStartOfMonth = (date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0);
  };
  const getEndOfMonth = (date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);
  };

  const startDateRange =
    agendaConfiguration.dateRangeOption === 1
      ? new Date(agendaConfiguration.dateRangeStartDate)
      : new Date();
  const endDateRange =
    agendaConfiguration.dateRangeOption !== 2
      ? agendaConfiguration.dateRangeOption === 1
        ? new Date(agendaConfiguration.dateRangeEndDate)
        : new Date(new Date().getTime() + agendaConfiguration.dateRangeDays * 24 * 60 * 60 * 1000)
      : undefined;

  const getBookings = async () => {
    try {
      const res = await api.bookingList({
        page: 1,
        limit: 1000,
        startDate: getStartOfMonth(currentMonth),
        endDate: getEndOfMonth(currentMonth),
        name: null,
        email: null,
        phone: null,
        completed: '',
        userId: user?.sub ? user?.sub : null,
        extension,
      });
      setBookings(res.bookings);
    } catch (error) {
      console.error(error);
      if (error instanceof ApiError) {
        errorContext.setError(error.message);
      } else {
        throw error;
      }
    }
  };

  const monthChange = async () => {
    setLoading(true);
    await getBookings();
    await getGoogleCalendarEvents();
    await getRecurrencesDates();
    setLoading(false);
  };

  useEffect(() => {
    if (currentMonth) {
      monthChange();
      if (currentMonth !== prevMonth.current) {
        setSelectedDate(undefined);
        prevMonth.current = currentMonth;
      }
    }
  }, [currentMonth]);

  const [recurrencesDates, setRecurrencesDates] = useState<DayWithHours[]>([]);
  const getRecurrencesDates = useCallback(async () => {
    try {
      const { firstDay, lastDay } = getMonthBoundaries(currentMonth);
      const res = await api.allRecurrenceDates(firstDay, lastDay, extension);
      setRecurrencesDates(res);
    } catch (error) {
      console.error(error);
      if (error instanceof ApiError) {
        errorContext.setError(error.message);
      } else {
        throw error;
      }
    }
  }, [currentMonth]);

  const [availableHours, setAvailableHours] = useState<{ [day: number]: string[] } | null>(null);
  useEffect(() => {
    if (localUser && currentMonth) {
      setAvailableHours(
        buildHoursofTheDaysOfTheMonthObject(
          currentMonth.getMonth(),
          startDateRange,
          endDateRange ? endDateRange : null,
          {
            ...agendaConfiguration,
            weeklySchedule: agendaConfiguration.overwriteAvailability
              ? agendaConfiguration.weeklySchedule
              : localUser.weeklySchedule,
            busyDays: agendaConfiguration.overwriteAvailability
              ? agendaConfiguration.busyDays
              : localUser.busyDays,
            specificDays: agendaConfiguration.overwriteAvailability
              ? agendaConfiguration.specificDays
              : localUser.specificDays,
          },
          bookings
            ? bookingsToDayWithHours(
                bookings,
                agendaConfiguration.timeBeforeSessions,
                agendaConfiguration.timeAfterSessions,
                localTimeZone
              )
            : [],
          googleCalendarEvents
            ? googleEventsToDayWithHours(googleCalendarEvents, 0, 0, localTimeZone)
            : [],
          recurrencesDates,
          forceAvailable
        )
      );
    }
  }, [
    bookings,
    googleCalendarEvents,
    currentMonth,
    agendaConfiguration,
    localUser,
    recurrencesDates,
  ]);
  return (
    localUser &&
    (extension ? (
      <>
        <DayPicker
          mode="single"
          selected={selectedDate}
          onSelect={setSelectedDate}
          disabled={[
            (date) => {
              if (loading) return true;
              if (!date) return true;
              if (availableHours === null) return true;
              if (!availableHours[date.getDate()]) {
                return date.getDate() !== selectedDate?.getDate();
              }
              return availableHours[date.getDate()] && availableHours[date.getDate()].length === 0;
            },
          ]}
          modifiers={{
            highlighted: (date) => date.toDateString() === selectedDate?.toDateString(),
            today: false,
          }}
          modifiersClassNames={{
            highlighted: 'day-highlighted',
          }}
          style={{
            paddingBottom: '12px',
          }}
          month={currentMonth}
          onMonthChange={(month) => setCurrentMonth(month)}
          startMonth={
            new Date(new Date().getTime() + agendaConfiguration.minimumNotice * 1000) <
            startDateRange
              ? startDateRange
              : new Date(new Date().getTime() + agendaConfiguration.minimumNotice * 1000)
          }
          endMonth={endDateRange}
        />
        {availableHours && selectedDate && (
          <div
            style={{
              height: dayPickerHeight || '200px',
              overflowY: 'auto',
              padding: '0 12px',
            }}
          >
            {availableHours &&
              selectedDate &&
              currentMonth.getMonth() === selectedDate.getMonth() &&
              availableHours[selectedDate.getDate()].map((hour) => {
                return (
                  <Button
                    key={hour + selectedDate.toISOString}
                    variant={selectedHour === hour ? 'filled' : 'outline'}
                    size="md"
                    style={{
                      display: 'block',
                      width: '100%',
                      marginBottom: '16px',
                    }}
                    onClick={() => {
                      setSelectedHour(hour);
                    }}
                  >
                    {hour}
                  </Button>
                );
              })}
          </div>
        )}
        {loading && (
          <Center
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translateX(-50%) translateY(-50%)',
            }}
          >
            <Loader></Loader>
          </Center>
        )}
      </>
    ) : (
      <>
        <DayPicker
          mode="single"
          selected={selectedDate}
          onSelect={setSelectedDate}
          disabled={[
            (date) => {
              if (loading) return true;
              if (!date) return true;
              if (availableHours === null) return true;
              if (!availableHours[date.getDate()]) {
                return date.getDate() !== selectedDate?.getDate();
              }
              return availableHours[date.getDate()].length === 0;
            },
          ]}
          modifiers={{
            highlighted: (date) => date.toDateString() === selectedDate?.toDateString(),
            today: false,
          }}
          modifiersClassNames={{
            highlighted: 'day-highlighted',
          }}
          style={{
            paddingBottom: '12px',
          }}
          month={currentMonth}
          onMonthChange={(month) => setCurrentMonth(month)}
          startMonth={
            new Date(new Date().getTime() + agendaConfiguration.minimumNotice * 1000) <
            startDateRange
              ? startDateRange
              : new Date(new Date().getTime() + agendaConfiguration.minimumNotice * 1000)
          }
          endMonth={endDateRange}
        />
        {loading && (
          <Center
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translateX(-50%) translateY(-50%)',
            }}
          >
            <Loader></Loader>
          </Center>
        )}
        {availableHours && selectedDate && (
          <div
            style={{
              margin: '0 25%',
              paddingTop: '16px',
              paddingLeft: '8px',
              paddingRight: '8px',
              borderTop: 'solid 1px lightgray',
              height: '200px',
              overflowY: 'auto',
            }}
          >
            {availableHours[selectedDate.getDate()].map((hour) => {
              return (
                <Button
                  variant="outline"
                  style={{ marginBottom: '16px', display: 'block', width: '100%' }}
                  size="md"
                  key={hour + selectedDate.toISOString}
                >
                  {hour}
                </Button>
              );
            })}
          </div>
        )}
      </>
    ))
  );
}
